import { useGetManagementUserCompaniesQuery, useGetUserCompaniesQuery } from '@company/services/company';
import { ICompany } from '@company/types/company';
import { Select } from '@components/utility/selects';
import { usePrefixedNavigate } from '@hooks/usePrefixedNavigate';
import AddIcon from '@mui/icons-material/Add';
import { Button } from '@mui/material';
import Divider from '@mui/material/Divider/Divider';
import { SelectChangeEvent } from '@mui/material/Select/Select';
import { comparator } from '@utils/common';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

interface ICompanyHeaderProps {
    withSelect?: boolean;
    className?: string;
}

const CompanyHeader = ({ withSelect = true, className }: ICompanyHeaderProps) => {
    const [searchParams] = useSearchParams();
    const isDashboard = useMemo(() => window.location.pathname.includes('/dashboard/'), []);

    const navigate = usePrefixedNavigate();
    const { data: userCompaniesResponse, isLoading: isUserCompaniesLoading } = useGetUserCompaniesQuery(
        {},
        {
            skip: isDashboard,
        }
    );
    const { data: managementUserCompaniesResponse, isLoading: isManagementUserCompaniesLoading } =
        useGetManagementUserCompaniesQuery({}, { skip: !isDashboard });
    const userCompanies = userCompaniesResponse as ICompany[];
    const managementUserCompanies = managementUserCompaniesResponse as ICompany[];
    const isLoading = useMemo(
        () => isUserCompaniesLoading || isManagementUserCompaniesLoading,
        [isManagementUserCompaniesLoading, isUserCompaniesLoading]
    );

    const companies = useMemo(
        () => (isDashboard ? managementUserCompanies : userCompanies) ?? [],
        [isDashboard, managementUserCompanies, userCompanies]
    );
    const userCompaniesSelect = useMemo(
        () => companies?.map((company) => ({ label: company.name, value: company.id })) ?? [],
        [companies]
    );

    const selectedCompany = useMemo(
        () =>
            searchParams.get('id') && !isLoading
                ? companies.find((company) => company.id === searchParams.get('id'))
                : companies.slice().sort((a, b) => -comparator(a, b, 'creation_date'))[0],
        [isLoading, searchParams, companies]
    );

    const handleCompanyChange = useCallback(
        (e: SelectChangeEvent<unknown>) => {
            const tab = searchParams.get('tab');
            navigate(`${isDashboard ? '/dashboard' : ''}/companies?id=${e.target.value}${tab ? `&tab=${tab}` : ''}`);
        },
        [isDashboard, navigate, searchParams]
    );
    return (
        <div className={clsx('gap-5 flex flex-col md:justify-start md:items-center md:flex-row', className)}>
            <div className="flex flex-col sm:flex-row gap-5">
                <h1 className={clsx('font-bold', { 'lg:text-5xl text-3xl': !isDashboard, 'text-4xl': isDashboard })}>
                    Company Overview
                </h1>
                {withSelect ? (
                    <>
                        <Divider
                            orientation="vertical"
                            sx={{ borderRightWidth: 'medium', color: 'black', height: '2.5rem' }}
                            className="bg-slate-400 hidden md:block"
                            flexItem
                        />
                        <Select
                            name="company"
                            size="small"
                            label="Company"
                            className="max-w-[150px] sm:max-w-[200px] min-w-[100px]"
                            items={userCompaniesSelect ?? []}
                            value={selectedCompany?.id ?? ''}
                            fcSX={{ size: 'small', variant: 'standard' }}
                            type="default"
                            onChange={handleCompanyChange}
                            loading={isLoading}
                        />
                    </>
                ) : null}
            </div>
            <Button
                startIcon={<AddIcon />}
                component={Link}
                to={`/app${isDashboard ? '/dashboard' : ''}/companies/new`}
            >
                Create company
            </Button>
        </div>
    );
};

export { CompanyHeader };
